import { DRAWING_WIDTH, DRAWING_HEIGHT } from "./coordinates.js";
/**
 * <DrawingCanvas>: wires the M2 drawing pipeline end-to-end.
 *
 * The make-or-break component that connects user input to pixels. Before
 * this existed, every piece was built (Canvas, usePointerHandlers,
 * drawingEngine, fillBucket, toolStore) but nothing was wired together,
 * the user could not draw.
 *
 * Architecture (M4 batch 7, strokes-store-as-source-of-truth):
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ DrawingCanvas                                                │
 *   │                                                              │
 *   │   activeTool / activeColor / brushSize  ← nanostore subs     │
 *   │   $finalizedStrokes                     ← nanostore sub      │
 *   │                                                              │
 *   │   ┌──────────────────┐         ┌──────────────────────────┐  │
 *   │   │ committedCanvas  │  blit→  │  <Canvas> (visible)      │  │
 *   │   │ (offscreen)      │         │   - DPR-scaled ctx       │  │
 *   │   │  rebuilt from    │         │   - usePointerHandlers   │  │
 *   │   │  $finalizedStrokes         │     attached via props   │  │
 *   │   └──────────────────┘         └──────────────────────────┘  │
 *   │           ▲                              ↓                   │
 *   │           │  store-watcher effect        │                   │
 *   │           │  (clear + replay log)        ↓                   │
 *   │   $finalizedStrokes (nanostore)  onStrokeStart/Sample/End    │
 *   │           ▲                              │                   │
 *   │           │   appendStroke()             │                   │
 *   │           └──────────────────────────────┘                   │
 *   │                                                              │
 *   │                       ┌──────┴───────────┴──────────┐        │
 *   │                       │ in-progress stroke buffers  │        │
 *   │                       │   Map<pointerId, {...}>     │        │
 *   │                       └─────────────────────────────┘        │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Per-sample render: clear visible canvas → blit committed → draw
 * in-progress stroke on top. On stroke end: write a FinalizedStroke into
 * `$finalizedStrokes`. The store-watcher effect rebuilds the committed
 * bitmap from the log and re-blits.
 *
 * Why a log instead of "just append to the bitmap"? Undo, clear, and
 * reconnect-hydrate all want to operate on a structured log. With the log
 * as the one place strokes live, the same plumbing handles drawer-local strokes,
 * server-broadcast strokes (commit 3), and STROKE_UNDO / STROKE_CLEAR
 * broadcasts (commit 3): every mutator just touches the store.
 *
 * Fill operations are stored alongside strokes. Replay uses the same fixed
 * 1600 by 1200 bitmap on every client, including after undo or reconnect.
 */

import { useRef, useEffect, useCallback } from "preact/hooks";
import { useStore } from "@nanostores/preact";
import { Canvas } from "./Canvas.js";
import { usePointerHandlers, type PointerSample } from "./usePointerHandlers.js";
import {
  applyDprTransform,
  drawStrokePath,
  drawFinalStroke,
  clearCanvas,
} from "./drawingEngine.js";
import { applyFill } from "./fillBucket.js";
import { strokeToSvgPath, type StrokePoint } from "./strokeToSvgPath.js";
import { $activeTool, $activeColor, $brushSize, BRUSH_SIZE_PX } from "../tools/toolStore.js";
import {
  $finalizedStrokes,
  $strokeResetNonce,
  $provisionalStrokes,
  appendStroke,
  appendFill,
  type CanvasOp,
} from "./strokesStore.js";
import {
  encodeStrokeStart,
  encodeStrokeBatch,
  encodeStrokeEnd,
  encodeColorIdx,
  type DecodedSample,
} from "@drawlol/shared";
import type { BrandColor } from "@drawlol/shared";
import type { BrushSizeIndex } from "../tools/toolStore.js";

/**
 * Monotonically-increasing local stroke id counter (a u32). Used as the
 * `strokeIdNonce` in the binary STROKE_START / STROKE_BATCH / STROKE_END
 * frames sent to the server (M4 batch 7 commit 2) AND as the canonical
 * `strokeId` (stringified) for the local `$finalizedStrokes` store.
 *
 * Restart at a random u32-safe value on module load so two accidentally-
 * mounted DrawingCanvas instances in the same tab (e.g. tests) don't
 * collide. Pure-shared modules can't do this, but the client can.
 *
 * Wraparound: `& 0xffffffff` keeps the counter inside u32 forever, a
 * looping wraparound is fine since the server treats strokeIds as opaque
 * strings; collisions would require ~4B strokes from one client, which
 * isn't a realistic concern in one session.
 */
let _localStrokeIdCounter = Math.floor(Math.random() * 0x7fffffff);

function mintLocalStrokeNonce(): number {
  _localStrokeIdCounter = (_localStrokeIdCounter + 1) & 0xffffffff;
  return _localStrokeIdCounter;
}

/**
 * Maximum samples buffered before a STROKE_BATCH frame is flushed. Picked
 * so the frame fits comfortably inside ~150 bytes (9 header + N×13 sample).
 * Server has no upper bound: it just appends to the per-stroke buffer.
 */
const STROKE_BATCH_FLUSH_THRESHOLD = 10 as const;

/** In-progress stroke captured between pointerdown and pointerup. */
type InProgressStroke = {
  pointerType: string;
  samples: StrokePoint[];
  /** Frozen at start. Lets mid-stroke tool/color/size swaps be ignored. */
  color: BrandColor;
  sizeIdx: BrushSizeIndex;
  isEraser: boolean;
  /**
   * Numeric u32 nonce used for binary STROKE_START / STROKE_BATCH /
   * STROKE_END frames. The stringified form is what the local store and
   * server reducer use as the strokeId. Minted at stroke start.
   */
  nonce: number;
  /**
   * Samples buffered for the next STROKE_BATCH flush. Distinct from
   * `samples` (which keeps every sample for the final svg-path bake) so a
   * flush only ships the deltas since the prior batch.
   */
  pendingBatch: DecodedSample[];
  /** STROKE_BATCH seq counter; increments per batch flushed to the server. */
  nextBatchSeq: number;
};

type DrawingCanvasProps = {
  /**
   * When true, the canvas renders incoming strokes (committed bitmap + any
   * future STROKE_END_BROADCAST replays) but discards all pointer input.
   * Used by the M4 batch-6b drawing phase: audience members see the
   * drawer's live canvas without being able to draw themselves.
   *
   * Implementation: pointer handlers are skipped (passed as `undefined` to
   * `<Canvas>`); the in-progress stroke buffer is never populated, so
   * `repaint()` only paints committed strokes. Resize + DPR handling stay
   * wired since the audience still needs the canvas sized to its parent.
   *
   * Default: false (free-draw + drawer-role behaviour).
   */
  readOnly?: boolean;
  /**
   * M4 batch 7 commit 2: drawer's outbound binary frame sender. Wired
   * from the WS client (`wsClient.sendBinary`) by the parent route. When
   * provided, every stroke emits:
   *
   *   pointerdown          → STROKE_START  (color, brushSize, nonce)
   *   pointermove (×N)     → STROKE_BATCH  (flushed periodically)
   *   pointerup            → STROKE_BATCH (final flush) + STROKE_END (svg path)
   *
   * When omitted (tests, free-draw single-user, readOnly audience), the
   * canvas operates locally only: the strokes still land in the local
   * `$finalizedStrokes` store; nothing crosses the wire.
   */
  sendBinary?: (buf: ArrayBuffer) => boolean;
  /**
   * v4 T1 (D5): drawer's outbound flood-fill sender. Wired by the parent to
   * `wsClient.sendFrame({ kind: "STROKE_FILL", ... })`. Called on a fill click
   * with the op id the canvas just appended optimistically, so the server's
   * FILL_BROADCAST echo dedups. When omitted, the fill is local-only (free-draw
   * single-user, tests): it still lands in `$finalizedStrokes`.
   */
  sendFill?: (op: { strokeId: string; point: { x: number; y: number }; color: BrandColor }) => void;
};

/**
 * The wired drawing surface. Mount inside FreeDrawRoom, produces strokes
 * on user input. Audience clients in M4 game mode pass `readOnly` so the
 * canvas becomes a passive replay surface.
 */
export function DrawingCanvas({ readOnly = false, sendBinary, sendFill }: DrawingCanvasProps = {}) {
  const visibleRef = useRef<HTMLCanvasElement>(null);
  const visibleCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  /** Offscreen canvas holding all finalized strokes. */
  const committedRef = useRef<HTMLCanvasElement | null>(null);
  const committedCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  /**
   * Most-recent DPR applied to both canvases. Needed by repaint/clear since
   * `clearCanvas()` has to re-install the DPR transform after resetting to
   * identity, and it shouldn't have to recompute it from canvas dimensions
   * (M2.fix(coords): the old `canvas.width / 1600` inference was wrong).
   */
  const dprRef = useRef(1);
  /** Active strokes keyed by pointerId. */
  const inProgressRef = useRef<Map<number, InProgressStroke>>(new Map());

  const activeTool = useStore($activeTool);
  const activeColor = useStore($activeColor);
  const brushSize = useStore($brushSize);
  /**
   * M4 batch 7: ref to the latest finalized strokes log. We subscribe to
   * `$finalizedStrokes` directly via `store.listen()` rather than `useStore`
   * because `@nanostores/preact`'s `useStore` debounces re-renders via
   * `setTimeout`, which adds an async edge that fights both the
   * `replayCommittedFromLog` path AND the test harness. The direct listener
   * fires synchronously on every `set()`, lets us update the ref, and then
   * we invoke the replay/repaint imperatively from within the listener.
   */
  const finalizedStrokesRef = useRef($finalizedStrokes.get());
  // Live mid-stroke render (audit 2026-06-11): provisional remote strokes,
  // painted only on readOnly canvases. Refs because repaint() is memoized.
  const provisionalRef = useRef($provisionalStrokes.get());
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  /**
   * v4 T1b: the exact op list currently composited into the committed bitmap.
   * Lets the store-watcher paint only the appended tail when ops were purely
   * appended (the common case) instead of clearing + re-running every prior
   * flood-fill on each new stroke. A full replay resets this to the live log.
   */
  const paintedRef = useRef<ReadonlyArray<CanvasOp>>([]);

  // Refs for the latest tool/color/size, read inside pointer handlers so
  // we always pick up the freshest value without re-binding handlers.
  const toolRef = useRef(activeTool);
  toolRef.current = activeTool;
  const colorRef = useRef(activeColor);
  colorRef.current = activeColor;
  const sizeRef = useRef(brushSize);
  sizeRef.current = brushSize;
  // M4 batch 7 commit 2: same pattern for the binary sender so pointer
  // handlers don't need to re-bind on prop change.
  const sendBinaryRef = useRef(sendBinary);
  sendBinaryRef.current = sendBinary;
  // v4 T1: same latched-ref pattern for the fill sender.
  const sendFillRef = useRef(sendFill);
  sendFillRef.current = sendFill;

  /**
   * Rebuild the committed bitmap from the latest finalized log. Used by
   * both the store-watcher effect (on log change) and `handleResize` (when
   * ctx first becomes available after layout). Safe to call repeatedly,
   * each invocation clears the bitmap and replays from scratch.
   */
  /**
   * Paint one op onto the committed ctx. A fill samples whatever pixels sit
   * beneath it, so it must run after the ops below it in the log are already
   * painted: which both the full replay (ordered loop) and the incremental
   * append (paints the tail onto the up-to-date bitmap) guarantee.
   */
  const paintOp = useCallback((cctx: CanvasRenderingContext2D, op: CanvasOp) => {
    if (op.kind === "fill") {
      applyFill(cctx, op.point.x, op.point.y, op.color, 1);
    } else {
      drawFinalStroke(cctx, op.path, op.color, op.brushSize, op.isEraser);
    }
  }, []);

  const replayCommittedFromLog = useCallback(() => {
    const committed = committedRef.current;
    const cctx = committedCtxRef.current;
    if (!committed || !cctx) return;
    clearCanvas(cctx, 1);
    // v4 T1: replay strokes AND fills in their logged order.
    const log = finalizedStrokesRef.current;
    for (const op of log) paintOp(cctx, op);
    // v4 T1b: the committed bitmap now reflects the full log, record it so the
    // store-watcher can take the incremental fast path on the next append.
    paintedRef.current = log;
  }, [paintOp]);

  /**
   * Repaint the visible canvas: clear, blit committed strokes, then draw
   * any in-progress strokes on top. Called per pointermove sample.
   *
   * Bound to the latest committed-canvas via refs; safe to memoize.
   */
  const repaint = useCallback(() => {
    const ctx = visibleCtxRef.current;
    const committed = committedRef.current;
    if (!ctx || !committed) return;

    clearCanvas(ctx, dprRef.current);

    // Scale the shared bitmap to the visible backing store in physical pixels.
    // Restore the drawing-space transform before painting live strokes.
    if (committed.width > 0 && committed.height > 0) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(committed, 0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.restore();
    }

    // Draw each in-progress stroke on top.
    for (const stroke of inProgressRef.current.values()) {
      const lastSample = stroke.samples.at(-1);
      const lastPressure = lastSample?.[2] ?? 0;
      drawStrokePath(
        ctx,
        stroke.samples,
        stroke.color,
        stroke.sizeIdx,
        stroke.isEraser,
        stroke.pointerType,
        lastPressure,
      );
    }

    // Live mid-stroke render (audit 2026-06-11): readOnly canvases ALSO draw
    // the remote drawer's provisional strokes, STROKE_BATCH samples that used
    // to be dropped, leaving the audience canvas empty until pointerup. The
    // drawer's own echo is in this store too but never painted here (its
    // canvas isn't readOnly; it draws the live stroke via inProgressRef).
    if (readOnlyRef.current) {
      for (const p of provisionalRef.current) {
        if (p.points.length === 0) continue;
        const pts: StrokePoint[] = p.points.map((s) => [s.x, s.y, s.pressure]);
        const last = p.points[p.points.length - 1];
        drawStrokePath(
          ctx,
          pts,
          p.color,
          p.brushSize,
          p.isEraser,
          p.points[0]?.pointerType ?? "mouse",
          last?.pressure ?? 0,
        );
      }
    }
  }, []);

  /** Keep fills and finalized ink on one canonical bitmap on every client. */
  const handleResize = useCallback(
    (physW: number, physH: number, _dpr: number) => {
      if (physW <= 0 || physH <= 0) return;
      const ctx = visibleRef.current?.getContext("2d");
      if (!ctx) return;
      visibleCtxRef.current = ctx;
      dprRef.current = physW / DRAWING_WIDTH;
      applyDprTransform(ctx, dprRef.current);
      if (!committedRef.current) {
        const committed = document.createElement("canvas");
        committed.width = DRAWING_WIDTH;
        committed.height = DRAWING_HEIGHT;
        committedRef.current = committed;
      }
      const cctx = committedRef.current.getContext("2d");
      if (!cctx) return;
      committedCtxRef.current = cctx;
      applyDprTransform(cctx, 1);
      replayCommittedFromLog();
      repaint();
    }, [replayCommittedFromLog, repaint],
  );

  const onStrokeStart = useCallback(
    (pt: { x: number; y: number }, pointerType: string, pressure: number, pointerId: number) => {
      if (toolRef.current === "fill") {
        // Fill is handled in onStrokeSample (which fires with the same
        // first point): see below. Skip starting a stroke buffer.
        return;
      }
      const isEraser = toolRef.current === "eraser";
      const color = colorRef.current;
      const sizeIdx = sizeRef.current;
      const nonce = mintLocalStrokeNonce();
      inProgressRef.current.set(pointerId, {
        pointerType,
        samples: [[pt.x, pt.y, pressure]],
        color,
        sizeIdx,
        isEraser,
        nonce,
        pendingBatch: [],
        nextBatchSeq: 0,
      });
      // M4 batch 7 commit 2: emit STROKE_START. encodeColorIdx maps the
      // BrandColor + eraser flag to the 9-state palette index used on the
      // wire. Throws on an unknown BrandColor, we trust the toolStore to
      // only ever set palette-valid colors.
      const sb = sendBinaryRef.current;
      if (sb) {
        sb(encodeStrokeStart(sizeIdx, encodeColorIdx(color, isEraser), nonce));
      }
    },
    [],
  );

  const onStrokeSample = useCallback(
    (sample: PointerSample, pointerId: number) => {
      // Fill: only the very first sample matters; flood-fill the committed
      // canvas at the hit point and re-blit. Future samples in the same
      // gesture are ignored.
      if (toolRef.current === "fill") {
        if (sample.dtMs !== 0) return; // first-sample only
        // v4 T1 (D5): a fill is a real op, not a fire-and-forget local raster.
        // Mint an op id, append the fill to the store (the store-watcher replay
        // paints it in order), and send STROKE_FILL so the server logs + fans
        // it out. The server echo dedups against this same op id. No payload
        // (sample.x/y) is lost: the fill point rides the frame.
        const strokeId = String(mintLocalStrokeNonce());
        const point = { x: sample.x, y: sample.y };
        const color = colorRef.current;
        appendFill({ strokeId, point, color });
        sendFillRef.current?.({ strokeId, point, color });
        repaint();
        return;
      }

      const stroke = inProgressRef.current.get(pointerId);
      if (!stroke) return;
      // dtMs=0 is the synthetic first sample already pushed in onStrokeStart.
      if (sample.dtMs === 0 && stroke.samples.length === 1) return;
      stroke.samples.push([sample.x, sample.y, sample.pressure]);
      // M4 batch 7 commit 2: also accumulate for the next STROKE_BATCH.
      // Coordinates ride the wire in CSS-pixel space (same as locally), the
      // server doesn't care about coordinate units; audience clients use
      // them for live-render, which is a follow-up batch. Pressure clamps
      // to [0,1]; pointerType is the original PointerEvent string.
      stroke.pendingBatch.push({
        x: sample.x,
        y: sample.y,
        pressure: sample.pressure,
        pointerType: (stroke.pointerType === "pen" || stroke.pointerType === "touch"
          ? stroke.pointerType
          : "mouse") as DecodedSample["pointerType"],
      });
      if (stroke.pendingBatch.length >= STROKE_BATCH_FLUSH_THRESHOLD) {
        const sb = sendBinaryRef.current;
        if (sb) {
          sb(encodeStrokeBatch(stroke.nonce, stroke.nextBatchSeq, stroke.pendingBatch));
        }
        stroke.nextBatchSeq += 1;
        stroke.pendingBatch = [];
      }
      repaint();
    },
    [repaint],
  );

  const onStrokeEnd = useCallback(
    (pointerId: number) => {
      const stroke = inProgressRef.current.get(pointerId);
      if (!stroke) return;
      inProgressRef.current.delete(pointerId);

      const baseWidth = BRUSH_SIZE_PX[stroke.sizeIdx];
      const svgPath = strokeToSvgPath(stroke.samples, baseWidth);

      // M4 batch 7 commit 2: flush any trailing samples + emit STROKE_END
      // so the server sees the canonical SVG path. The local store append
      // below uses `String(stroke.nonce)` as the strokeId, the same value
      // we just sent on the wire: so when the server broadcasts the
      // stroke back the store's dedup is a no-op.
      const sb = sendBinaryRef.current;
      if (sb) {
        if (stroke.pendingBatch.length > 0) {
          sb(encodeStrokeBatch(stroke.nonce, stroke.nextBatchSeq, stroke.pendingBatch));
          stroke.nextBatchSeq += 1;
          stroke.pendingBatch = [];
        }
        if (svgPath) {
          sb(encodeStrokeEnd(stroke.nonce, svgPath));
        }
      }

      if (svgPath) {
        // Route the finalized stroke through the store instead of baking
        // directly into the committed bitmap. The store-watcher
        // subscription rebuilds the bitmap from the log. This keeps undo /
        // clear / hydrate / broadcast-replay all working off one source of
        // truth.
        appendStroke({
          strokeId: String(stroke.nonce),
          path: svgPath,
          color: stroke.color,
          brushSize: stroke.sizeIdx,
          isEraser: stroke.isEraser,
        });
      }
      // Repaint clears the in-progress overlay; the store-watcher
      // subscription handles the committed bitmap.
      repaint();
    },
    [repaint],
  );

  const pointerHandlers = usePointerHandlers(visibleRef, {
    onStrokeStart,
    onStrokeSample,
    onStrokeEnd,
  });

  // Defensive: if the component unmounts mid-stroke, drop the buffer.
  useEffect(() => {
    return () => {
      inProgressRef.current.clear();
    };
  }, []);

  /**
   * M4 batch 7: subscribe directly to `$finalizedStrokes` and replay the
   * log into the committed bitmap on every change. We use `store.listen()`
   * rather than `useStore` because the latter debounces via setTimeout
   * (see `@nanostores/preact/index.js`), that breaks the synchronous
   * "mutate → replay → blit" path the canvas needs.
   *
   * When ctx isn't ready yet (first mount before `handleResize`), the
   * replay no-ops; `handleResize` calls `replayCommittedFromLog` once
   * ctx is set up, so nothing is lost.
   */
  useEffect(() => {
    finalizedStrokesRef.current = $finalizedStrokes.get();
    const unlisten = $finalizedStrokes.listen((next) => {
      finalizedStrokesRef.current = next;
      const cctx = committedCtxRef.current;
      const painted = paintedRef.current;
      // v4 T1b incremental fast path: ops were purely APPENDED onto the prefix
      // already composited into the committed bitmap. Paint only the new tail
      //a new stroke no longer re-runs every prior flood-fill (the O(pixels)
      // cost the naive full-replay-per-change path incurred). Detected by: the
      // log grew AND the last previously-painted op is still at its index.
      const isAppendOnly =
        cctx !== null &&
        painted.length > 0 &&
        next.length > painted.length &&
        next[painted.length - 1]?.strokeId === painted[painted.length - 1]?.strokeId;
      if (isAppendOnly && cctx) {
        for (let i = painted.length; i < next.length; i++) {
          const op = next[i];
          if (op) paintOp(cctx, op);
        }
        paintedRef.current = next;
      } else {
        // undo (log shrank) / clear / hydrate (prefix replaced) / first append
        // / ctx-not-ready → the existing bitmap is invalid; full replay. Dropping
        // a held in-progress stroke on a CLEAR/hydrate is driven by the separate
        // $strokeResetNonce effect below, NOT by an empty log here, an UNDO that
        // empties the log must leave an active stroke alone.
        replayCommittedFromLog();
      }
      repaint();
    });
    return unlisten;
  }, [replayCommittedFromLog, repaint, paintOp]);

  /**
   * Live mid-stroke render (audit 2026-06-11): repaint as remote STROKE_BATCH
   * samples accumulate. Only readOnly canvases subscribe, the drawer paints
   * its own live stroke via the pointer pipeline and must not double-draw its
   * echo. ~1 repaint per batch (every ~10 drawer samples), cheap.
   */
  useEffect(() => {
    provisionalRef.current = $provisionalStrokes.get();
    if (!readOnly) return;
    const unlisten = $provisionalStrokes.listen((next) => {
      provisionalRef.current = next;
      repaint();
    });
    return unlisten;
  }, [readOnly, repaint]);

  /**
   * v4 T3: drop any in-progress stroke when the canvas is RESET, a CLEAR or a
   * full hydrate (clearStrokes/hydrateStrokes bump $strokeResetNonce). Without
   * this, a stroke held mid-clear keeps re-painting onto the wiped committed
   * bitmap until pointerup (a lingering overlay). Keyed off the reset signal,
   * NOT an empty $finalizedStrokes, so an UNDO that merely empties the log
   * leaves an active stroke alone (aborting it would drop the user's live ink
   * and orphan its server-side buffer → disconnect-synth resurrection). Safe:
   * usePointerHandlers' strokeStartRef still holds the pointerId, so a later
   * move/up resolves to a clean no-op (inProgressRef.get → undefined → early
   * return; nothing is drawn or appended).
   */
  useEffect(() => {
    const unlisten = $strokeResetNonce.listen(() => {
      inProgressRef.current.clear();
      repaint();
    });
    return unlisten;
  }, [repaint]);

  // readOnly path: skip pointer handlers entirely so audience clients
  // can't accidentally start strokes (or buffer pointer state). The
  // repaint/resize plumbing stays wired so future STROKE_END_BROADCAST
  // events (M4 batch 7+) still update the committed bitmap.
  if (readOnly) {
    return <Canvas ref={visibleRef} onResize={handleResize} style={{ cursor: "default" }} />;
  }

  return (
    <Canvas
      ref={visibleRef}
      onResize={handleResize}
      onPointerDown={pointerHandlers.onPointerDown}
      onPointerMove={pointerHandlers.onPointerMove}
      onPointerUp={pointerHandlers.onPointerUp}
      onPointerCancel={pointerHandlers.onPointerCancel}
      onLostPointerCapture={pointerHandlers.onLostPointerCapture}
    />
  );
}
